import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  ActivityIndicator,
  Platform,
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Colors from '../../../constants/Colors';
import Layout from '../../../constants/Layout';
import { formatPhoneNumber } from '../../../utils/formatPhone';
import AddressSelector from '../AddressSelector';
import Selector from '../../form/Selector';
import MaterialSwitch from '../../../utils/switch';
import Svg from '../../Svg';
import MyFloatingLabelInput from '../../form/MyFloatingLabelInput';
import PlusMinus from '../../form/PlusMinus';
import MyDateOfBirthInput from '../../form/MyDateOfBirthInput';
import forceComponentUpdate from '../../../utils/forceComponentUpdate';
import NavBar from '../../NavBar';
import Checkbox from '../../form/Checkbox';
import { formatField, getQuestionByUUID } from '../../../utils/additionalInfo';
import HouseholdSize from '../../form/HouseholdSize/HouseholdSize';
import { UserInfoProps } from './UserInfoProps';
import PtfKeyboardAvoidingView from '../../PtfKeyboardAvoidingView';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
  },
  spinnerModal: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    zIndex: 1,
  },
  mainContent: {
    maxWidth: 720,
    width: '100%',
  },
  contentContainer: {
    color: Colors.primaryColor,
    backgroundColor: Colors.lightBackground,
    paddingBottom: 32,
    position: 'relative',
  },
  infoCard: {
    padding: 16,
    paddingTop: 32,
    borderBottomColor: Colors.grayBorder,
    borderBottomWidth: 1,
    backgroundColor: Colors.white,
  },
  infoCardRow: {
    flexDirection: Layout.isCompactScreen ? 'column' : 'row',
    alignItems: Layout.isCompactScreen ? 'flex-start' : 'center',
    justifyContent: 'space-between',
  },
  detailsRow: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  checkboxCardRow: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  smallRow: {
    width: Layout.isCompactScreen ? '100%' : '50%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dobRow: {
    marginTop: Layout.isCompactScreen ? 0 : -5,
    alignItems: 'flex-start',
  },
  secondRow: {
    width: Layout.isCompactScreen ? '100%' : '100%',
    flexDirection: Layout.isCompactScreen ? 'column' : 'row',
    alignItems: Layout.isCompactScreen ? 'stretch' : 'center',
    justifyContent: Layout.isCompactScreen ? 'flex-start' : 'flex-start',
  },
  inputElement: {
    flex: 1,
    paddingRight: 16,
    marginBottom: 16,
  },
  phoneElement: {
    marginTop: 4,
    paddingBottom: 8,
  },
  mixedInputRow: {
    alignItems: 'flex-start',
  },
  zipCodeContainer: {
    width: Layout.isCompactScreen ? '50%' : '25%',
  },
  emailContainer: {
    flex: Layout.isCompactScreen ? 1 : 2,
    flexDirection: 'row',
    width: Layout.isCompactScreen ? '100%' : '50%',
  },
  spacerContainer: {
    display: Layout.isCompactScreen ? 'none' : 'flex',
  },
  placesContainer: {
    // This allows the Google Places suggestions to sit above the other form elements
    zIndex: 1,
  },
  inputContainer: {
    flex: 1,
    marginLeft: 0,
  },
  dobContainer: {
    marginTop: 12,
  },
  inputField: {
    paddingLeft: 0,
  },
  floatingLabel: {
    position: 'absolute',
    top: 0,
    color: Colors.grayText,
    fontSize: 14,
    marginTop: -8,
  },
  subheader: {
    paddingHorizontal: Layout.fullWidth < 732 ? 16 : 0, // 720 + 2 * 16
    paddingTop: 24,
    paddingBottom: 12,
    backgroundColor: Colors.lightBackground,
  },
  subheaderText: {
    fontSize: 14,
    color: Colors.mediumBlue,
    fontWeight: 'bold',
  },
  sectionHeaderText: {
    fontSize: 16,
    color: Colors.black,
    fontWeight: '600',
    paddingBottom: 12,
    paddingTop: 18,
  },
  label: {
    color: Colors.grayText,
    fontSize: 14,
  },
  familyLabel: {
    color: Colors.grayText,
    fontSize: 14,
    marginBottom: 4,
  },
  specLabel: {
    color: Colors.grayText,
    fontSize: 14,
    width: '100%',
  },
  viewWithRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  notEditableContainter: {
    marginTop: 16,
  },
  pickupSwitchRow: {
    paddingRight: Layout.isCompactScreen ? 0 : 16,
    marginBottom: Layout.isCompactScreen ? 16 : 0,
  },
  desSec: {
    padding: 10,
  },
  viewWithXtaPadding: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 36,
  },
  detailText: {
    fontSize: 16,
    lineHeight: 24,
    marginTop: 8,
    color: Colors.veryDarkGray,
  },
  halfWidth: {
    width: '50%',
  },
  halfWidthLeft: {
    width: '50%',
    paddingRight: 16,
  },
  birthdayField: {
    paddingBottom: 8,
    borderBottomColor: Colors.inputGrayBorder,
    borderBottomWidth: 1,
  },
  birthdayLabel: {
    marginTop: -8,
    flexDirection: 'column',
    justifyContent: 'flex-end',
  },
  inputPlusIcon: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  iconContainer: {
    flexDirection: 'column',
    justifyContent: 'center',
    paddingLeft: 4,
    paddingBottom: 4,
  },
  pickupNameRow: {
    flexDirection: Layout.isCompactScreen ? 'column' : 'row',
    alignItems: Layout.isCompactScreen ? 'flex-start' : 'flex-end',
  },
  nameRow: {
    marginBottom: Layout.isCompactScreen ? 16 : 0,
    marginRight: Layout.isCompactScreen ? 0 : 16,
    width: Layout.isCompactScreen ? '75%' : '50%',
  },
  saveChangesContainer: {
    position: 'absolute',
    backgroundColor: Colors.mediumBlue,
    height: 56,
    top: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 16,
  },
  saveChangesText: {
    color: Colors.white,
    textTransform: 'uppercase',
    fontWeight: 'bold',
    marginRight: 16,
  },
  emailVerificationSentContainer: {
    width: '100%',
    backgroundColor: Colors.darkGreen,
    padding: 16,
  },
  emailVerificationSentText: {
    color: Colors.white,
    fontSize: Layout.isLarge ? 16 : 14,
    lineHeight: 20,
  },
  emailLinkContainer: {
    paddingTop: 16,
  },
  emailLinkText: {
    fontSize: Layout.isLarge ? 16 : 14,
    lineHeight: 20,
    color: Colors.darkGreen,
    textDecorationLine: 'underline',
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  errorText: {
    color: Colors.brightRed,
    fontSize: Layout.isLarge ? 16 : 14,
  },
  regularText: {
    fontSize: Layout.isLarge ? 16 : 14,
    lineHeight: 20,
    maxWidth: 720,
  },
  textRow: {
    marginTop: 12,
    marginBottom: 12,
    lineHeight: 20,
    borderColor: Colors.accentColor,
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
  },
  noteContent: {
    fontFamily: 'roboto-regular',
  },
});

class UserInfo extends Component {
  static propTypes = {
    translation: PropTypes.objectOf(PropTypes.any).isRequired,
    locale: PropTypes.string.isRequired,
    actions: PropTypes.objectOf(PropTypes.any).isRequired,
    profile: PropTypes.objectOf(PropTypes.any).isRequired,
    registration: PropTypes.objectOf(PropTypes.any).isRequired,
  };

  /**
   * @param {UserInfoProps} props
   */
  constructor(props) {
    super();
    const { translation } = props;
    const year2000 = 946750000;

    const capitalize = (string) => {
      return string.charAt(0).toUpperCase() + string.slice(1);
    };

    this.state = {
      errors: {},
      user: Object.assign({}, props.profile),
      originalUser: Object.assign({}, props.profile),
      year2000,
      showSpinner: false,
      showChangeLanguage: false,
      showSaveChanges: false,
      showBirthdateModal: false,
      pushNotificationRegistration: false,
      labels: {
        first_name: capitalize(translation.app_personal_edittext_firstname),
        last_name: capitalize(translation.app_personal_edittext_lastname),
        birth_date: capitalize(translation.app_personal_edittext_dob),
        zip_code: translation.app_personal_edittext_zipcode,
        phone: translation.app_personal_edittext_phone,
        email: translation.email_address,
        emergency_contact_name: translation.fragment_profile_edittext_emergencyname,
        emergency_contact_phone: translation.fragment_profile_edittext_emergencyphone,
        pick_up_name: translation.fragment_profile_edittext_pickupname,
        pick_up_phone: translation.fragment_profile_edittext_pickupphone,
        pickup_food: capitalize(translation.fragment_profile_switch_pickupfood),
        receive_notifications: translation.fragment_profile_switch_receivenotifications,
        change_personal_info: translation.fragment_profile_textview_changepersonalinfo,
        emergency_contact: capitalize(translation.fragment_profile_textview_emergencycontact),
        personal_details: capitalize(translation.fragment_profile_textview_personaldetails),
        family_details: capitalize(translation.family_details),
        optional_details: capitalize(translation.optional_details),
        address_street_1: capitalize(translation.address_street_1),
        address_street_2: capitalize(translation.address_street_2),
        city: capitalize(translation.city),
        state: capitalize(translation.state),
        zipcode: capitalize(translation.zipcode),
        gender: capitalize(translation.gender),
        relationship_status: capitalize(translation.relationship_status),
        ethnicity: capitalize(translation.ethnicity),
        country_of_birth: capitalize(translation.country_of_birth),
        employment: capitalize(translation.employment),
        highest_education_obtained: capitalize(translation.highest_education_obtained),
        food_preferences: capitalize(translation.food_preferences),
        housing_details: capitalize(translation.housing_details),
        housing_status: capitalize(translation.housing_status),
        special_circumstances: capitalize(translation.special_circumstances),
        needs_assistance: capitalize(translation.needs_assistance),
        additional_optional_details: capitalize(translation.additional_optional_details),
        service_details: capitalize(translation.custom_field_food_pickup),
        title: translation.fragment_profile_textview_title,
        update_text: translation.fragment_profile_textview_changepersonalinfo,
        save_changes: translation.fragment_profile_button_savechanges,
        send_another_verification: translation.send_another_verification_email,
        email_in_use: translation.email_in_use,
        email_invalid: translation.email_invalid,
        email_verification_sent: translation.email_verification_sent,
        yes: translation.app_button_yes,
        no: translation.app_button_no,
        baby_supplies: capitalize(translation.baby_supplies),
      },
    };
  }

  componentDidMount = () => {
    const { actions, profile } = this.props;
    this.setState({ user: Object.assign({}, profile) });
    actions.clearEnterEmail();
  };

  componentDidUpdate = () => {
    const { errors, user } = this.state;
    const { registration } = this.props;
    const { enterEmail, user: newUser } = registration;
    if (user.id !== newUser.id) {
      this.setState({
        user: Object.assign({}, newUser),
        originalUser: Object.assign({}, newUser),
      });
    }
  };

  validate = (field, value) => {
    const { year2000 } = this.state;
    switch (field) {
      case 'birth_date':
        if (!value) return true;
        return Number.isInteger(value) && value !== year2000;
      case 'first_name':
      case 'last_name':
        return value && value !== '' && value.length > 0;
      case 'adult_family_members':
      case 'child_family_members':
      case 'senior_family_members':
        if (!value) return true;
        return parseInt(value, 10) >= 0 && parseInt(value, 10) < 11;
      case 'phone':
      case 'pick_up_phone':
      case 'emergency_contact_phone':
        if (value) {
          const phone = value.replace(/\D/g, '');
          return phone.length === 10;
        }
        return true;
      case 'zip_code':
        return value && value.length === 5;
      case 'email':
        return (
          value &&
          value.length > 3 &&
          value.indexOf('@') !== -1 &&
          value.indexOf('.') !== -1 &&
          value.indexOf('.') < value.length - 1
        );
      default:
        return true;
    }
  };

  somethingChanged = (user) => {
    const { profile } = this.props;
    const updates = {};
    const isEmptyString = (value) => value === '' || value === null || value == undefined;

    Object.keys(profile).forEach((key) => {
      // array comparison kept simple
      if (Array.isArray(profile[key])) {
        if (JSON.stringify(profile[key]) != JSON.stringify(user[key])) {
          updates[key] = user[key];
        }
      } else {
        if (profile[key] !== user[key] && !(isEmptyString(profile[key]) && isEmptyString(user[key]))) {
          updates[key] = user[key];
        }
      }
    });
    return { changes: updates, changed: !!Object.keys(updates).length };
  };

  setChanges(field, value) {
    const { user, errors } = this.state;
    if (this.validate(field, value)) {
      errors[field] = false;
    } else {
      errors[field] = true;
    }
    if (field == 'food_pick_up') {
      user[field] = !!value;
    } else {
      user[field] = value;
    }
    const updates = this.somethingChanged(user);
    this.setState({ user, showSaveChanges: updates.changed, errors });
  }

  setSelectorChanges = (field, value) => {
    this.setChanges(field, value);
  };

  setFoodPickup = () => {
    this.setChanges('food_pick_up', true);
  };

  removeFoodPickup = () => {
    this.setChanges('food_pick_up', false);
  };

  cancelShowSaveChanges = () => {
    this.setState({ showSaveChanges: false });
  };

  confirmSaveChanges = () => {
    const { user, originalUser, errors } = this.state;
    const { actions } = this.props;
    const updates = this.somethingChanged(user);
    if (updates.changed) {
      const fields = [
        'birth_date',
        'first_name',
        'last_name',
        'zip_code',
        'adult_family_members',
        'child_family_members',
        'senior_family_members',
      ];
      let formOk = true;
      fields.forEach((field) => {
        if (updates.changes[field] && !this.validate(field, updates.changes[field])) {
          formOk = false;
          errors[field] = true;
        } else {
          errors[field] = false;
        }
      });
      // we cannot sumbit an empty email if we do not have a verified phone
      if (updates.changes.email !== undefined) {
        if (!updates.changes.email || updates.changes.email === '') {
          if (!originalUser.phone || originalUser.phone === '' || !originalUser.otp_verified) {
            formOk = false;
            errors.email = true;
          }
        }
      }
      this.setState({ user, errors });
      if (formOk) {
        const changedData = updates.changes;
        // ensure we have 0 for empty family member fields
        Object.keys(changedData).forEach((key) => {
          if (key.indexOf('_family_members') !== -1 && !changedData[key]) {
            changedData[key] = 0;
          }
        });
        actions.updateUser(changedData);
        this.setState({ showSaveChanges: false });
      }
    }
  };

  backToProfile = () => {
    const { navigation } = this.props;
    this.setState({ user: {}, errors: {}, showSaveChanges: false });
    navigation.navigate('landing');
  };

  logOut = () => {
    const { actions } = this.props;
    actions.logout();
  };

  // we run this after editing email
  checkEmail = () => {
    const { actions } = this.props;
    const { user, originalUser, errors } = this.state;
    const doSend = false;
    errors.email = false;
    if (!user.email || user.email === '') {
      if (!originalUser.phone || originalUser.phone === '' || !originalUser.otp_verified) {
        errors.email = true;
      }
    }
    this.setState({ errors });
    if (!errors.email) {
      this.setState({ showSpinner: true });
      actions.checkEmail(user.email, doSend, () => {
        this.setState({ showSpinner: false });
      });
    }
  };

  // we run this on clicking the link
  verifyEmail = () => {
    const { actions } = this.props;
    const { user } = this.state;
    this.setState({ showSpinner: true });
    const doSend = true;
    actions.checkEmail(user.email, doSend, () => {
      this.setState({ showSpinner: false });
    });
    setTimeout(() => {
      actions.clearEnterEmail();
    }, 6000);
  };

  renderInputElement = (key, keyboardType) => {
    const { locale, translation } = this.props;
    const { errors, user, labels } = this.state;
    let value = user[key];
    if (value == null || value == undefined) value = '';
    if (keyboardType === 'phone-pad') {
      value = formatPhoneNumber(value);
      keyboardType = 'default';
    } else if (key === 'zip_code' && value) {
      value = value.replace(/\D/g, '');
    }
    let disabled = false;
    if (key === 'pick_up_name' || key === 'pick_up_phone') {
      if (!user.food_pick_up) disabled = true;
    }

    if (value || value == 0) value = value.toString();
    let label = labels[key];

    if (key === 'baby_supplies') {
      return (
        <View style={styles.inputContainer}>
          <Text style={styles.familyLabel}>{label}</Text>
          <PlusMinus
            key={`${key}-${value}`}
            value={Number(value)}
            onChange={(text) => {
              this.setChanges(key, text);
            }}
            min={0}
            max={8}
          />
        </View>
      );
    } else if (key == 'birth_date') {
      return (
        <View style={styles.dobContainer}>
          <MyDateOfBirthInput
            translation={translation}
            onSelect={(text) => {
              this.setChanges(key, text);
            }}
            date={value}
            label={label}
            locale={locale}
            width="100%"
            error={errors[key] || null}
          />
        </View>
      );
    }

    return (
      <View style={styles.inputContainer}>
        <MyFloatingLabelInput
          label={label}
          style={styles.inputField}
          onChangeText={(text) => {
            this.setChanges(key, text);
          }}
          onEndEditing={Platform.OS !== 'web' && key === 'email' ? this.checkEmail : null}
          onBlur={Platform.OS === 'web' && key === 'email' ? this.checkEmail : null}
          value={value}
          keyboardType={keyboardType}
          disabled={disabled}
          width="100%"
          error={errors[key]}
          testID={`user-info-input-state-${key}`}
        />
      </View>
    );
  };

  showBirthdateModal = () => {
    this.setState({ showBirthdateModal: true });
  };

  cancelBirthdate = () => {
    this.setState({ showBirthdateModal: false });
  };

  updateBirthdate = (value) => {
    const { translation } = this.props;
    const invalid = translation.app_invalid_error;
    const { user, errors } = this.state;
    if (!this.validate('birth_date', value)) {
      errors.birth_date = `${invalid} ${translation.app_personal_edittext_dob}`;
    } else {
      errors.birth_date = null;
    }
    this.setState({ errors, showBirthdateModal: false });
    this.setChanges('birth_date', value);
  };

  onAddressSelect = (addressDetails) => {
    Object.keys(addressDetails).forEach((field) => {
      this.setChanges(field, addressDetails[field]);
    });
  };

  handleForceUpdate = () => {
    forceComponentUpdate(this);
  };

  render() {
    const { showSaveChanges, user, labels, errors, showSpinner } = this.state;
    const { translation, registration } = this.props;

    const phoneIconColor = user.otp_verified ? Colors.profileVerified : Colors.iconLightGray;
    const phoneIcon = <Svg icon="verified" color={phoneIconColor} height={20} width={20} />;
    const emailIconColor = user.email_verified ? Colors.profileVerified : Colors.iconLightGray;
    const emailIcon = <Svg icon="verified" color={emailIconColor} height={20} width={20} />;

    let emailError = null;
    const { enterEmail } = registration;
    if (enterEmail.results && enterEmail.results.email) {
      if (!enterEmail.results.email_valid) {
        emailError = translation.email_invalid;
      } else if (enterEmail.results.in_use) {
        emailError = translation.email_invalid;
      }
    }
    if (!emailError && errors.email) {
      emailError = translation.email_invalid;
    }

    const showEmailVerifyLinkSent = enterEmail.results && enterEmail.results.verification_token_sent;
    let emailEditable = false;
    if (!user.email_verified) emailEditable = true;

    let whereToUpdate = null;
    if (user.otp_verified && user.phone) {
      if (user.email_verified && user.email) {
        whereToUpdate = translation.need_to_change_email_or_phone;
      } else {
        whereToUpdate = translation.need_to_change_phone;
      }
    } else if (user.email_verified && user.email) {
      whereToUpdate = translation.need_to_change_email;
    }

    const dietaryPreferencesQuestion = getQuestionByUUID('dietary_preferences');

    return (
      <View style={styles.container}>
        <NavBar title={labels.personal_details} onPressBack={this.backToProfile} />

        {showEmailVerifyLinkSent ? (
          <View style={styles.emailVerificationSentContainer}>
            <Text style={styles.emailVerificationSentText}>{labels.email_verification_sent}</Text>
          </View>
        ) : null}

        {showSpinner ? (
          <View style={styles.spinnerModal}>
            <ActivityIndicator size="large" color={Colors.mediumBlue} />
          </View>
        ) : null}

        <PtfKeyboardAvoidingView style={{ flex: 1 }} keyboardVerticalOffset={60}>
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={styles.contentContainer}
            keyboardShouldPersistTaps={'handled'}
            keyboardDismissMode="on-drag"
          >
            <View style={styles.subheader}>
              <Text style={styles.subheaderText}>{labels.personal_details}</Text>
            </View>
            <View style={styles.infoCard}>
              <View style={styles.infoCardRow}>
                <View style={styles.smallRow}>
                  <View style={styles.inputElement}>{this.renderInputElement('first_name')}</View>
                  <View style={styles.inputElement}>{this.renderInputElement('last_name')}</View>
                </View>
                <View style={[styles.smallRow, styles.dobRow]}>
                  <View style={styles.inputElement}>{this.renderInputElement('birth_date')}</View>
                  <View style={[styles.inputElement, styles.phoneElement]}>
                    <Text style={styles.label}>{labels.phone}</Text>
                    <View style={styles.inputPlusIcon}>
                      <Text style={styles.detailText}>{formatPhoneNumber(user.phone)}</Text>
                      <View style={styles.iconContainer}>{phoneIcon}</View>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.infoCardRow}>
                <View style={styles.secondRow}>
                  {emailEditable ? (
                    <View style={[styles.inputElement, styles.emailContainer]}>
                      {this.renderInputElement('email', 'email-address')}
                      <View style={styles.iconContainer}>{emailIcon}</View>
                    </View>
                  ) : (
                    <View style={[styles.inputElement, styles.phoneElement]}>
                      <Text style={styles.label}>{labels.email}</Text>
                      <View style={styles.inputPlusIcon}>
                        <Text style={styles.detailText}>{user.email}</Text>
                        <View style={styles.iconContainer}>{emailIcon}</View>
                      </View>
                    </View>
                  )}
                  {emailError ? (
                    <Text style={styles.errorText}>{emailError}</Text>
                  ) : (
                    <View style={[styles.inputElement, styles.spacerContainer]} />
                  )}
                </View>
              </View>

              {user.email && !user.email_verified && !emailError && !errors.email ? (
                <View style={styles.infoCardRow}>
                  <View style={styles.emailLinkContainer}>
                    <Text style={styles.emailLinkText} onPress={this.verifyEmail}>
                      {labels.send_another_verification}
                    </Text>
                  </View>
                </View>
              ) : null}

              {whereToUpdate ? (
                <View style={styles.infoCardRow}>
                  <View style={styles.emailLinkContainer}>
                    <Text style={styles.regularText}>{whereToUpdate}</Text>
                  </View>
                </View>
              ) : null}
            </View>

            <View style={styles.subheader}>
              <Text style={styles.subheaderText}>{labels.family_details}</Text>
            </View>
            <View style={styles.infoCard}>
              <View style={styles.infoCardRow}>
                <HouseholdSize
                  familyMemberCounts={{
                    adultFamilyMembers: user['adult_family_members'] ?? 0,
                    childFamilyMembers: user['child_family_members'] ?? 0,
                    seniorFamilyMembers: user['senior_family_members'] ?? 0,
                  }}
                  zipcode={user['zip_code']}
                  state={user['state']}
                  language={registration.language}
                  labelStyle={styles.familyLabel}
                  onChange={(key, value) => {
                    this.setChanges(key, value);
                  }}
                ></HouseholdSize>
              </View>
              <View style={styles.infoCardRow}>
                <View style={styles.textRow}>
                  <Text style={styles.noteContent}>
                    {translation.fragment_registration_family_textview_notecontent}
                  </Text>
                </View>
              </View>

              <View style={styles.infoCardRow}>
                <View style={styles.smallRow}>
                  <View style={styles.inputElement}>
                    {this.renderInputElement('baby_supplies', 'number-pad')}
                  </View>
                  <View style={styles.inputElement} />
                </View>
              </View>
            </View>

            <View style={styles.subheader}>
              <Text style={styles.subheaderText}>{labels.optional_details}</Text>
            </View>
            <View style={styles.infoCard}>
              <View style={styles.infoCardRow}>
                <Text style={styles.sectionHeaderText}>{labels.address_street_1}</Text>
              </View>
              <View style={[styles.infoCardRow, styles.placesContainer, styles.mixedInputRow]}>
                <View style={[styles.smallRow, styles.placesContainer]}>
                  <View style={styles.inputElement}>
                    <AddressSelector
                      translation={translation}
                      onSelect={this.onAddressSelect}
                      addressStreet1={user.address_street_1}
                      state={user.state}
                      city={user.city}
                      handleForceUpdate={this.handleForceUpdate}
                      onChange={(newValue) => this.setSelectorChanges('address_street_1', newValue)}
                      testID={'user-info-input-address_street_1'}
                    />
                  </View>
                </View>
                <View style={styles.smallRow}>
                  <View style={styles.inputElement}>{this.renderInputElement('address_street_2')}</View>
                </View>
              </View>
              <View style={styles.infoCardRow}>
                <View style={[styles.smallRow, styles.mixedInputRow]}>
                  <View style={styles.inputElement}>{this.renderInputElement('city')}</View>
                  <View style={styles.inputElement}>
                    <Selector
                      onChange={this.setSelectorChanges}
                      label={translation.state}
                      field={'state'}
                      currentValue={user['state']}
                      dataset={'state'}
                      translation={translation}
                      testID={'user-info-input-state'}
                    />
                  </View>
                </View>
                <View style={styles.smallRow}>
                  <View style={[styles.inputElement, styles.zipCodeContainer]}>
                    {this.renderInputElement('zip_code', 'number-pad')}
                  </View>
                </View>
              </View>

              <View style={styles.infoCardRow}>
                <Text style={styles.sectionHeaderText}>{labels.additional_optional_details}</Text>
              </View>
              <View style={styles.infoCardRow}>
                <View style={styles.smallRow}>
                  <View style={styles.inputElement}>
                    <Selector
                      onChange={this.setSelectorChanges}
                      label={translation.gender}
                      field={'gender'}
                      currentValue={user['gender']}
                      dataset={'gender'}
                      translation={translation}
                    />
                  </View>
                </View>

                <View style={styles.smallRow}>
                  <View style={styles.inputElement}>
                    <Selector
                      onChange={this.setSelectorChanges}
                      label={translation.relationship_status}
                      field={'relationship_status'}
                      currentValue={user['relationship_status']}
                      dataset={'relationship_status'}
                      translation={translation}
                    />
                  </View>
                </View>
              </View>
              <View style={styles.infoCardRow}>
                <View style={styles.smallRow}>
                  <View style={styles.inputElement}>
                    <Selector
                      onChange={this.setSelectorChanges}
                      label={translation.ethnicity}
                      field={'ethnicity'}
                      currentValue={user['ethnicity']}
                      dataset={'ethnicity'}
                      translation={translation}
                    />
                  </View>
                </View>

                <View style={styles.smallRow}>
                  <View style={styles.inputElement}>
                    <Selector
                      onChange={this.setSelectorChanges}
                      label={translation.employment}
                      field={'employment'}
                      currentValue={user['employment']}
                      dataset={'employment'}
                      translation={translation}
                    />
                  </View>
                </View>
              </View>
              <View style={styles.infoCardRow}>
                <View style={styles.smallRow}>
                  <View style={styles.inputElement}>
                    <Selector
                      onChange={this.setSelectorChanges}
                      label={translation.highest_education_obtained}
                      field={'education'}
                      currentValue={user['education']}
                      dataset={'education'}
                      translation={translation}
                    />
                  </View>
                </View>

                <View style={styles.smallRow}>
                  <View style={styles.inputElement}>
                    <Selector
                      onChange={this.setSelectorChanges}
                      label={translation.country_of_birth}
                      field={'country_of_birth'}
                      currentValue={user['country_of_birth']}
                      dataset={'country_of_birth'}
                      translation={translation}
                    />
                  </View>
                </View>
              </View>
              <View style={styles.infoCardRow}>
                <View style={styles.smallRow}>
                  <View style={styles.inputElement}>
                    <Selector
                      onChange={this.setSelectorChanges}
                      label={translation.can_prepare_food}
                      field={'food_preparation'}
                      currentValue={user['food_preparation']}
                      dataset={'yes_no'}
                      extraOptions={[
                        { value: 0, name: labels.no },
                        { value: 1, name: labels.yes },
                      ]}
                      translation={translation}
                    />
                  </View>
                </View>

                <View style={styles.smallRow}>
                  <View style={styles.inputElement}>
                    <Selector
                      onChange={this.setSelectorChanges}
                      label={translation.needs_assistance}
                      field={'needs_assistance'}
                      currentValue={user['needs_assistance']}
                      dataset={'yes_no'}
                      extraOptions={[
                        { value: 0, name: labels.no },
                        { value: 1, name: labels.yes },
                      ]}
                      translation={translation}
                    />
                  </View>
                </View>
              </View>

              <View style={styles.infoCardRow}>
                <Text style={styles.sectionHeaderText}>{labels.food_preferences}</Text>
              </View>
              <View style={styles.checkboxCardRow}>
                {dietaryPreferencesQuestion?.options.map((option) => (
                  <Checkbox
                    key={option.value}
                    checked={user.dietary_preferences?.includes(option.value)}
                    onPress={() =>
                      this.setChanges(
                        'dietary_preferences',
                        formatField(dietaryPreferencesQuestion, option.value, user)
                      )
                    }
                    label={translation[option.label]}
                  />
                ))}
              </View>

              <View style={styles.infoCardRow}>
                <Text style={styles.sectionHeaderText}>{labels.housing_details}</Text>
              </View>
              <View style={styles.smallRow}>
                <View style={styles.inputElement}>
                  <Selector
                    onChange={this.setSelectorChanges}
                    label={'Housing Status'}
                    field={'housing'}
                    currentValue={user['housing']}
                    dataset={'housing'}
                    translation={translation}
                  />
                </View>
              </View>
            </View>

            <View style={styles.subheader}>
              <Text style={styles.subheaderText}>{labels.service_details}</Text>
            </View>
            <View style={styles.infoCard}>
              <View style={styles.infoCardRow}>
                <View style={[styles.smallRow, styles.pickupSwitchRow]}>
                  <Text style={styles.detailText}>{labels.pickup_food}</Text>
                  <View>
                    <MaterialSwitch
                      onActivate={this.setFoodPickup}
                      onDeactivate={this.removeFoodPickup}
                      active={!!user.food_pick_up}
                    />
                  </View>
                </View>
                <View style={[styles.smallRow, styles.pickupNameRow]}>
                  <View style={styles.nameRow}>{this.renderInputElement('pick_up_name')}</View>
                  <View style={styles.nameRow}>{this.renderInputElement('pick_up_phone', 'phone-pad')}</View>
                </View>
              </View>
            </View>

            <View style={styles.subheader}>
              <Text style={styles.subheaderText}>{labels.emergency_contact}</Text>
            </View>
            <View style={styles.infoCard}>
              <View style={styles.infoCardRow}>
                <View style={[styles.smallRow, styles.nameRow]}>
                  {this.renderInputElement('emergency_contact_name')}
                </View>
                <View style={[styles.smallRow, styles.nameRow]}>
                  {this.renderInputElement('emergency_contact_phone', 'phone-pad')}
                </View>
              </View>
            </View>
          </ScrollView>
        </PtfKeyboardAvoidingView>

        {showSaveChanges && !emailError ? (
          <View style={styles.saveChangesContainer}>
            <TouchableOpacity onPress={this.cancelShowSaveChanges} style={styles.cancelSaveChanges}>
              <MaterialIcons name={'close'} size={24} color={Colors.white} />
            </TouchableOpacity>
            <TouchableOpacity onPress={this.confirmSaveChanges} style={styles.cancelSaveChanges}>
              <Text style={styles.saveChangesText}>{labels.save_changes}</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>
    );
  }
}

export default UserInfo;
